<script context="module" lang="ts">
  import type * as TPlotly from 'plotly.js';
  // Typescript magic. plotly.js is loaded from CDN, use @types/plotly.js namespace as type with 'typeof'
  declare var Plotly: typeof TPlotly;
</script>

<script lang="ts">
  import { getContext } from 'svelte';
  import { onMount, onDestroy }  from 'svelte/internal';
  import { getRequest } from '../Util/utils';
  import LibLoader from '@/Util/LibLoader.svelte';

  export let workout_id;

  let plotly: HTMLElement;
  let loading = true;

  let abort_ctrl;

  onDestroy(()=>{
    // Abort fetch when unloaded.
    abort_ctrl?.abort();
  });
  let error;

  async function initializeChart(){
    if (workout_id) {

      abort_ctrl = new AbortController();
      const { signal } = abort_ctrl; // TODO: Learn how this magic syntax works.

      let response;
      try {
        response = await fetch(`/workout/${workout_id}/data`, {
          ...getRequest(),
          signal
        }).then(r=>r.json());
      } catch (e) {
        loading = false;
        error = e;
        return;
      }
      let data: any[] = response.data ?? [];

      // Don't draw graph for empty workout.
      // Need at least 2 data points to draw graph.
      if (data.length < 2) {
        loading = false;
        error = true;
        return;
      }

      //let timezone = getTimezoneOffset()*60;
      let zeropad = (v) => ("0"+v).slice(-2);
      let ts = data.map(v=>{ return v.seconds; });
      //console.log(data);
      //console.log(response.stats);
      let pmax = Math.max(...data.map(v=>v.mpa));

      let series: TPlotly.Data[] = [
        {
          x: ts,
          y: Array.from({length: ts.length}, ()=>pmax),
          line: {
            width: 0,
          },
          fill: 'tonexty',
          hoverinfo: 'none',

          type: 'scattergl'
        }, {
          x: ts,
          y: data.map(v=>v.power),
          fillcolor: 'white',
          fill: 'tonexty',
          line: {
            width: 1.0,
            color: 'black',
          },
          hoverlabel: {
            bgcolor: "#6697cf",
            bordercolor: "black"
          },
          text: data.map(v=>{
            return `${v.description}<br>Power: <b>${Math.round(v.power)} W</b><br>Total duration: <b>${v.interval_duration} s</b>`;
          }),
          hovertemplate: "%{text}<extra></extra>",

          type: 'scattergl',
        }, {
          x: ts,
          y: data.map(v=>v.mpa),
          mode: 'lines+markers',
          line: {
            width: 3,
            color: '#cc33ff',
          },
          marker: {
            size: 3,
            //color: data.map(v=>v.mpaColor),
            color: data.map(v=>v.xssr),
            colorscale: [
              [0.0, '#e699ff'],
              [0.1875, '#cc33ff'],
              [1.0, '#13001a'],
            ],
            cmin: 0,
            cmax: 800,
          },
          hoverlabel: {
            bgcolor: "#6d75d9",
            bordercolor: "white",
          },
          text: data.map(v=>{
            return `<br>MPA: <b>${Math.round(v.mpa)} W</b><br>XSSR: <b>${Math.round(v.xssr)}</b>`;
          }),
          hovertemplate: "%{text}<extra></extra>",

          type: 'scattergl'
        }, {
          x: ts,
          y: [0, pmax],
          //z: [data.map(v=>v.powerColorValue), data.map(v=>v.powerColorValue)],
          z: [data.slice(0, -1).map(v=>v.powerColorValue)],
          //z: data.map(v=>v.powerColorValue),

          colorscale: [
            [0.0, '#bbbbbb'],
            [0.2, '#699cfe'],
            [0.4, '#50e3c2'],
            [0.6, '#79ff4c'],
            [0.600001, '#ffef4c'],
            [0.8, '#ff7d2a'],
            [1.0, '#ff0c00'],
          ],
          zmin: 0,
          zmax: 1,
          showscale: false,
          hoverinfo: 'none',

          type: 'heatmap',
          //zsmooth: 'best'
          zsmooth: false,
        },
      ];
      let n_labels = 6;
      let n_label_skip = Math.floor(ts.length / n_labels / 60)*60;
      loading = false;
      let label_x = (vs) => vs.map(v=>{
        let ss = zeropad(v % 60);
        let mm = Math.floor(v/60) % 60;
        let hh = Math.floor(v/3600);
        return hh > 0 ? `${hh}:${zeropad(mm)}:${ss}` : `${mm}:${ss}`;
      });

      let chart = await Plotly.newPlot(plotly, series, {
        margin: { t: 0, b: 0, l: 0, r: 0},
        showlegend: false,
        hovermode: "x",
        yaxis: {
          fixedrange: true,
          automargin: true,
        },
        xaxis: {
          spikecolor: 'black',
          spikedash: 'none',
          spikemode: 'across',
          spikesnap: 'cursor',
          spikethickness: 1,
          automargin: true,
          range: [ts[0], ts[ts.length-1]??0],
          tickmode: 'array',
        },
      }, {
        responsive: true,
        displayModeBar: false,
        //modeBarButtons: [],
        //modeBarButtonsToAdd: ['zoom2d', 'pan2d', 'autoScale2d', 'resetScale2d', 'toImage'],
      });

      let refresh_xlabels = (event)=>{
        //console.log(event);
        if (event && event['xaxis.range[0]'] == null) return; // Ignore events when xaxis range doesn't change.
        let x_min = ts[0];
        let x_max = ts[ts.length - 1];

        let x0 = chart.layout.xaxis.range[0];
        let x1 = chart.layout.xaxis.range[1];

        let rounds = [60*60, 30*60, 15*60, 10*60, 5*60, 60, 30, 10, 5, 1];
        let x_skip;
        if (x1 - x0 <= 0) x_skip = 1;
        else rounds.every(r=>{
          x_skip = Math.floor((x1-x0) / n_labels / r)*r;
          return x_skip == 0;
        });
        if (x_skip < 1) x_skip = 1;

        x0 = Math.floor(Math.max(x_min, x0) / x_skip)*x_skip;
        x1 = Math.floor(Math.min(x1, x_max) / x_skip)*x_skip;

        let x = x0;
        let vals = [];
        while (x <= x1) {
          vals.push(x);
          x += x_skip;
        }
        if (x_max - x1 > x_skip * 0.4) {
          vals.push(x_max);
        } else if (vals.length > 0) {
          vals[vals.length - 1] = x_max;
        }

        //console.log(`x0: ${x0}; x1: ${x1}; x_max: ${x_max}; x_skip: ${x_skip}`);

        Plotly.relayout(plotly, {
          'xaxis.tickvals': vals,
          'xaxis.ticktext': label_x(vals),
        });
      };
      refresh_xlabels(null);
      plotly.on('plotly_relayout', refresh_xlabels);
    }
  }

  // Refresh chart on sidebar toggle
  const jq = window.$;
  let doResize = () => {
    Plotly.Plots.resize(plotly);
  }
  let mounted = false
  onMount(()=>{
    mounted = true
  });
  onDestroy(()=>{
    jq(window).off('resize', doResize);
  });


  let isLoaded = false
  const loaded = () => {isLoaded = true} 
  $: if (mounted && isLoaded) {
    void initializeChart();
    jq(window).on('resize', doResize);
  }
</script>

<LibLoader url='https://cdn.plot.ly/plotly-latest.min.js' on:loaded={loaded} shouldLoadLib={() => !window.Plotly} />
{#if error}
  {error}
  <!-- Hide view if error loading chart data. -->
{:else}
<plotly style="height:400px; width: 100%; display: inline-block;" bind:this={plotly}>
  {#if loading}
  <div style="position: relative; width: 100%; height: 100%;">
    <div style="position: absolute; left: 50%; top: 50%; color: var(--color-xert-teal); font-size: 32px;" class="fa fa-circle-o-notch fa-spin"/>
  </div>
  {/if}
</plotly>
{/if}
